import { createContext, useState } from 'react';
import { MessageDescriptor } from 'react-intl';

import { ADEMLicenseInfoResp, AppAccelSettingsCommon, AddOnInfo, AppAccelAddonLicense } from '../data/queryResultsDefinition';
import Logger, { Level } from './Logger'
import { LicEntitlements } from '../data/sparkyDefs';

export enum CfgInitState {
    unknown = 'unknown',
    uninitialized = 'uninitialized',
    inprogress = 'inprogress',
    initialized = 'initialed',
    failed = 'failed',
}

export enum ToastAppearance {
    'clear' = 'clear',
    'info' = 'info',
    'warning' = 'warning',
    'error' = 'error',
    'success' = 'success',
}

// was put in for edit boxes but can be used for anything.
export enum InputAppearance {
    "error" = "error",
    "success" = "success",
    "default" = "default"
}

export enum PopupTypes {
    none = 'NONE',
    AdemTestingPopup = 'ADEM_TESTING_POPUP',
    AppAccelDisabledPopup = 'APP_ACCEL_DISABLE_POPUP',
}

export interface ADEMLicenseInfo extends ADEMLicenseInfoResp {
    in_grace_period: boolean        // note this is something we add to the licenseInfo
}

export interface AppAccelSettings extends AppAccelSettingsCommon {
    ademEnabled: boolean,
    ademPercentIndex: number,
}

export interface Addon_licenses {
    items: AddOnInfo[];
    includeAdem: boolean | undefined
    in_grace_period: boolean
    urlEnvModifier: string | undefined;
}

export const buildEnvUrl = (endpoint: string): string => {
    let data = contextSingleton.getInstance();

    let newUrl = data?.appAcclLicenseInfo?.urlEnvModifier ?? ''
    newUrl += endpoint;
    return newUrl;
}

export interface NaaGlobalData {
    currentTsgId: string,
    currentSubTenId: string | undefined,
    sysConfigCapable: boolean,
    appAccelEnabled: boolean,               // This is the current enablement state
    appAccelSettings: AppAccelSettings      // This is the settings as they appear on the page. Enablement can be set to enabled here
                                            // but because it hasn't been applied yet, it may be different that appAccelEnabled.
    uiInitialized: CfgInitState,
    authToken: string, 

    error: MessageDescriptor | undefined,
    toastStyle: ToastAppearance | undefined,
    toastMsg: string | undefined,
    currentPopup: PopupTypes,

    appAcclLicenseInfo: Addon_licenses,
    ademLicenseInfo: ADEMLicenseInfo | undefined,

    ademAvail: CfgInitState,
    ademBackwdCompat: boolean,
}

const NaaInitSettings: AppAccelSettings = {
   enabled: false, ports: [],  ademEnabled: false, ademPercentIndex: 0
}

const naaGlobalInitData: NaaGlobalData = {
    appAccelSettings: {...NaaInitSettings}, appAccelEnabled: NaaInitSettings.enabled,
    uiInitialized: CfgInitState.uninitialized, authToken: '', currentSubTenId: '',
    sysConfigCapable: false, error: undefined, currentTsgId: '', toastStyle: ToastAppearance.clear,
    toastMsg: '', ademAvail: CfgInitState.uninitialized, ademLicenseInfo: {} as ADEMLicenseInfo, 
    appAcclLicenseInfo: {} as Addon_licenses, currentPopup: PopupTypes.none, ademBackwdCompat: false
}

export type NaaContextType = {
    naaGlobal: NaaGlobalData;
    setSystemCapable: (enabled: boolean) => void;
    setAcclSettings: (settings: AppAccelSettings, savedToZos?: boolean) => void;
    setInitialized: (initialized: CfgInitState) => void;
    setAuthToken: (token: string) => void;
    setError: (msg: MessageDescriptor | undefined) => void;
    setCurrentTenantInfo: (tsgId: string, subTenId: string | undefined) => void;
    setToastData: ( toastStyle: ToastAppearance | undefined, toastMsg: string | undefined) => void;
    showPopup: (popupType: PopupTypes) => void;
    resetContext: () => void;

    setAdemAvail: (avail: CfgInitState) => void;
    setLicenseInfo: (appAcclLicenseInfo: Addon_licenses, ademLicenseInfo?: ADEMLicenseInfo) => void;
    setAdemData: (enabled: boolean, idx: number, backwardEnabled?: boolean) => void;   
                                                            // idx is the index in an array of percentage values
                                                            // The array is [5, 10, 15, 20] which are legal values
                                                            // for percentage.  To get from values to index 
                                                            //      idx = (percentage-5)/5;
                                                            // There are utility functions for this in shared/utilities.tsx
};

const contextSingleton = (() => {
    let naaContext: NaaGlobalData | undefined = undefined;
    let logLevel: Level = Level.ERROR;
    return {
        getInstance: (): NaaGlobalData => {
            if (naaContext === undefined) {
                naaContext = {...naaGlobalInitData}
            }

            return naaContext;
        }
    }
})()

export const NaaGlobalContext = createContext<NaaContextType | undefined>(undefined);

interface ContextProps {
    children: React.ReactNode;
}

const NaaContextProvider: React.FunctionComponent<ContextProps> = ({ children }) => {
    const [globalNaaData, setNaaGlobalData] = useState<NaaGlobalData>(naaGlobalInitData);
    const [logLevel, setLogLevel] = useState(Level.ERROR)

    const logState = (data: NaaGlobalData, routine: string): void => {
        if (logLevel > Level.ERROR) {
            Logger.info(`${routine}: Context data is ${JSON.stringify(data, null, 2)}`);
        }
    }

    const setNewLogLevel = (newLevel: Level): void => {
        setLogLevel(newLevel)
    }

    const setSysCapable = (capable: boolean): void => {
        let data = contextSingleton.getInstance();
        data.sysConfigCapable = !!capable;

        logState(data, `setSysCapable`);
        setNaaGlobalData( {...data} );
    }

    const setAcclSettings = (settings: AppAccelSettings, savedToZos?: boolean ): void => {
        let data = contextSingleton.getInstance();
        if (!settings.enabled) {
            settings = {...NaaInitSettings};
        }
        data.appAccelSettings = {...settings};
        if (savedToZos) {
            data.appAccelEnabled = settings.enabled;
        }

        logState(data, `setAcclSettings`);
        setNaaGlobalData( {...data} );
    }

    const setInitialized = (initialized: CfgInitState): void => {
        let data = contextSingleton.getInstance();
        data.uiInitialized = initialized;

        logState(data, `setInitialized`);
        setNaaGlobalData( {...data} );
    }

    const setAuthToken = (token: string): void => {
        let data = contextSingleton.getInstance();
        data.authToken = token;
        logState(data, `setAuthToken`);
        setNaaGlobalData( {...data} );
    }

    const setError = (msg: MessageDescriptor | undefined): void => {
        let data = contextSingleton.getInstance();
 
        data.error = msg;
        logState(data, `setError`);
        setNaaGlobalData( {...data} );
    }

    const setCurrentTenantInfo = (tsgId: string, subTenId: string | undefined): void => {
        let data = contextSingleton.getInstance();
        data.currentTsgId = tsgId;
        data.currentSubTenId = subTenId;

        logState(data, `setCurrentTenantInfo`);
        setNaaGlobalData( {...data} );
    }

    const setToastData = (toastStyle: ToastAppearance | undefined, toastMsg: string | undefined ) => {
        let data = contextSingleton.getInstance();
        data.toastStyle = toastStyle;
        data.toastMsg = toastMsg;
        logState(data, `setToastData`);
        setNaaGlobalData( {...data} );
    }

    const setAdemAvail = (avail: CfgInitState) => {
        let data = contextSingleton.getInstance();
        data.ademAvail = avail;
        logState(data, `setAdemAvail`);
        setNaaGlobalData( {...data} );
    }

    const setAdemData = (enabled: boolean, idx: number, backwardCompat?: boolean) => {
        let data = contextSingleton.getInstance();
        data.appAccelSettings.ademEnabled = enabled;
        data.appAccelSettings.ademPercentIndex = idx;
        data.ademBackwdCompat = !!backwardCompat;
        logState(data, `setAdemData`);
        setNaaGlobalData( {...data} );
    }

    const setLicenseInfo = (appAcclLicenseInfo: Addon_licenses, ademLicenseInfo?: ADEMLicenseInfo | undefined) => {
        let data = contextSingleton.getInstance();
        data.ademLicenseInfo = ademLicenseInfo;
        data.appAcclLicenseInfo = appAcclLicenseInfo;
        logState(data, `setAdemLicenseInfo`);
        setNaaGlobalData( {...data} );
    }

    const showPopup = (popupType: PopupTypes) => {
        let data = contextSingleton.getInstance();
        data.currentPopup = popupType;
        logState(data, `showPopup`);
        setNaaGlobalData( {...data} );
    }

    const resetContext = (): void => {
        let data: NaaGlobalData | undefined = contextSingleton.getInstance();

        data = undefined;
        data = contextSingleton.getInstance();

        setNaaGlobalData( {...data} );
    }

    const myContext: NaaContextType = {
        naaGlobal: globalNaaData,
        setSystemCapable: setSysCapable,
        setAcclSettings, 
        setInitialized,
        setAuthToken,
        setError,
        setCurrentTenantInfo,
        setToastData,
        showPopup,
        resetContext,
        setAdemAvail,
        setAdemData,
        setLicenseInfo,
    }
    
    return <NaaGlobalContext.Provider value={myContext}>{children}</NaaGlobalContext.Provider>
}

export default NaaContextProvider;